import { useCallback, useEffect, useMemo, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../../types/plans'
import { staffApi } from '../../../../api/staff'
import { isPatientDiagnosticsLinked } from './domain/diagnosticsLinked'
import { usePatientVatechCtCatalog } from './usePatientVatechCtCatalog'

export function usePatientDiagnosticsLinked({
  bootstrap,
  session,
  lkPatientId,
  misPatientId,
  enabled = true,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  lkPatientId: number | null
  misPatientId?: string | null
  enabled?: boolean
}) {
  const catalogHook = usePatientVatechCtCatalog({
    bootstrap,
    session,
    lkPatientId,
    misPatientId,
    enabled: enabled && Boolean(lkPatientId),
  })

  const [diagnocatData, setDiagnocatData] = useState<Awaited<
    ReturnType<typeof staffApi.getPatientDiagnocat>
  > | null>(null)
  const [diagnocatLoading, setDiagnocatLoading] = useState(false)

  const reloadDiagnocat = useCallback(async () => {
    if (!enabled || !lkPatientId) {
      setDiagnocatData(null)
      return
    }
    setDiagnocatLoading(true)
    try {
      const data = await staffApi.getPatientDiagnocat(bootstrap, session, lkPatientId)
      setDiagnocatData(data)
    } catch (error) {
      console.warn("diagnocat snapshot failed", error)
      setDiagnocatData((prev) => prev)
    } finally {
      setDiagnocatLoading(false)
    }
  }, [bootstrap, session, lkPatientId, enabled])

  useEffect(() => {
    void reloadDiagnocat()
  }, [reloadDiagnocat])

  const diagnosticsLinked = useMemo(
    () =>
      isPatientDiagnosticsLinked({
        diagnocat: diagnocatData,
        catalogPatientLinked: catalogHook.catalog?.patient_linked,
      }),
    [diagnocatData, catalogHook.catalog?.patient_linked],
  )

  return {
    diagnosticsLinked,
    diagnocatData,
    diagnocatLoading,
    catalogHook,
    reloadDiagnocat,
  }
}
